import { useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, X, Loader2 } from "lucide-react";

/**
 * Shared form fields for Create & Edit pilot dialogs.
 * - Inline partner creation via a visible "+ Nuevo partner" button (no longer hidden inside the Select).
 * - Duration field supports unit toggle: Semanas · Meses · Años (stored as `duration_weeks`).
 */
const UNIT_TO_WEEKS = { semanas: 1, meses: 4.345, años: 52 };

function weeksToDisplay(weeks) {
  if (!weeks && weeks !== 0) return { value: "", unit: "semanas" };
  const w = Number(weeks);
  if (w > 0 && w % 52 === 0) return { value: String(w / 52), unit: "años" };
  if (w > 0 && Math.round(w / 4.345) * 4.345 === w) return { value: String(Math.round(w / 4.345)), unit: "meses" };
  return { value: String(w), unit: "semanas" };
}

export function PilotFormFields({
  formData, setFormData, initiatives, partners,
  newPartnerName, setNewPartnerName,
  creatingPartner, onCreatePartner,
  idPrefix = "",
}) {
  const pid = (k) => (idPrefix ? `${idPrefix}-${k}` : k);

  // Local UI state for duration unit + visible value
  const initial = weeksToDisplay(formData.duration_weeks);
  const [durValue, setDurValue] = useState(initial.value);
  const [durUnit, setDurUnit] = useState(initial.unit);
  const [showPartnerInput, setShowPartnerInput] = useState(false);

  // When formData.duration_weeks changes from outside (e.g. opening edit dialog), sync local state.
  useEffect(() => {
    const next = weeksToDisplay(formData.duration_weeks);
    setDurValue(next.value);
    setDurUnit(next.unit);
  }, [formData.id]);

  const updateDuration = (value, unit) => {
    setDurValue(value);
    setDurUnit(unit);
    const num = parseFloat(value);
    if (isNaN(num) || num <= 0) {
      setFormData({ ...formData, duration_weeks: "" });
    } else {
      const weeks = Math.round(num * UNIT_TO_WEEKS[unit]);
      setFormData({ ...formData, duration_weeks: weeks });
    }
  };

  const handleCreatePartnerClick = () => {
    onCreatePartner((newId) => {
      setFormData({ ...formData, partner_id: newId });
      setShowPartnerInput(false);
    });
  };

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor={pid("initiative")}>Iniciativa vinculada *</Label>
        <Select
          value={formData.initiative_id}
          onValueChange={(value) => setFormData({ ...formData, initiative_id: value })}
        >
          <SelectTrigger id={pid("initiative")}><SelectValue placeholder="Selecciona una iniciativa" /></SelectTrigger>
          <SelectContent>
            {initiatives.map((init) => (
              <SelectItem key={init.id} value={init.id}>{init.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor={pid("name")}>Nombre del piloto *</Label>
        <Input
          id={pid("name")}
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Piloto de validación..."
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor={pid("objective")}>Objetivo</Label>
        <Textarea
          id={pid("objective")}
          value={formData.objective}
          onChange={(e) => setFormData({ ...formData, objective: e.target.value })}
          placeholder="¿Qué queremos lograr?"
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor={pid("hypothesis")}>Hipótesis a validar</Label>
        <Textarea
          id={pid("hypothesis")}
          value={formData.hypothesis}
          onChange={(e) => setFormData({ ...formData, hypothesis: e.target.value })}
          placeholder="Si hacemos X, entonces Y..."
          rows={2}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Partner with visible "+ Nuevo" toggle */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor={pid("partner")}>Partner</Label>
            {!showPartnerInput ? (
              <button
                type="button"
                onClick={() => setShowPartnerInput(true)}
                className="text-xs text-emerald-600 hover:text-emerald-700 font-medium inline-flex items-center gap-1"
                data-testid={pid("show-new-partner-btn")}
              >
                <Plus className="w-3 h-3" /> Nuevo
              </button>
            ) : (
              <button
                type="button"
                onClick={() => { setShowPartnerInput(false); setNewPartnerName(""); }}
                className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                data-testid={pid("cancel-new-partner-btn")}
              >
                <X className="w-3 h-3" /> Cancelar
              </button>
            )}
          </div>

          {!showPartnerInput ? (
            <Select
              value={formData.partner_id}
              onValueChange={(value) => setFormData({ ...formData, partner_id: value })}
            >
              <SelectTrigger id={pid("partner")} data-testid={pid("partner-select")}>
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                {partners.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-500">No hay partners. Crea uno con &quot;+ Nuevo&quot;.</div>
                ) : (
                  partners.map((partner) => (
                    <SelectItem key={partner.id} value={partner.id}>{partner.name}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex gap-2">
              <Input
                autoFocus
                placeholder="Ej: Turing, Microsoft, IBM…"
                value={newPartnerName}
                onChange={(e) => setNewPartnerName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreatePartnerClick();
                  }
                }}
                data-testid={pid("new-partner-input")}
              />
              <Button
                type="button"
                size="sm"
                disabled={creatingPartner || !newPartnerName.trim()}
                onClick={handleCreatePartnerClick}
                data-testid={pid("confirm-new-partner-btn")}
              >
                {creatingPartner ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              </Button>
            </div>
          )}
        </div>

        {/* Duration with unit toggle */}
        <div className="space-y-2">
          <Label htmlFor={pid("duration")}>Duración</Label>
          <div className="flex gap-2">
            <Input
              id={pid("duration")}
              type="number"
              value={durValue}
              onChange={(e) => updateDuration(e.target.value, durUnit)}
              min={1}
              step={durUnit === "años" ? "0.5" : "1"}
              placeholder={durUnit === "semanas" ? "12" : durUnit === "meses" ? "6" : "2"}
              className="flex-1"
              data-testid={pid("duration-value")}
            />
            <Select
              value={durUnit}
              onValueChange={(unit) => updateDuration(durValue, unit)}
            >
              <SelectTrigger className="w-[110px]" data-testid={pid("duration-unit")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="semanas">Semanas</SelectItem>
                <SelectItem value="meses">Meses</SelectItem>
                <SelectItem value="años">Años</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {formData.duration_weeks ? (
            <p className="text-xs text-slate-500">≈ {formData.duration_weeks} semanas almacenadas</p>
          ) : null}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={pid("cost")}>Coste estimado (€)</Label>
        <Input
          id={pid("cost")}
          type="number"
          value={formData.cost_estimated}
          onChange={(e) => setFormData({ ...formData, cost_estimated: e.target.value })}
          placeholder="15000"
        />
      </div>

      {/* ── Enriched fields (Feb 2026) ─────────────────────────────── */}
      <div className="pt-4 border-t space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Extendido</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor={pid("sponsor")}>Sponsor</Label>
            <Input
              id={pid("sponsor")}
              value={formData.sponsor || ""}
              onChange={(e) => setFormData({ ...formData, sponsor: e.target.value })}
              placeholder="Área / Persona sponsor"
              data-testid={`${pid("sponsor")}-input`}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={pid("sponsor-email")}>Sponsor email</Label>
            <Input
              id={pid("sponsor-email")}
              type="email"
              value={formData.sponsor_email || ""}
              onChange={(e) => setFormData({ ...formData, sponsor_email: e.target.value })}
              placeholder="sponsor@valoriza.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={pid("business-line")}>Línea de servicio</Label>
            <Input
              id={pid("business-line")}
              value={formData.business_line || ""}
              onChange={(e) => setFormData({ ...formData, business_line: e.target.value })}
              placeholder="Waste / Water / Green / …"
              data-testid={`${pid("business-line")}-input`}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={pid("real-cost")}>Coste real (€)</Label>
            <Input
              id={pid("real-cost")}
              type="number"
              value={formData.real_cost_eur || ""}
              onChange={(e) => setFormData({ ...formData, real_cost_eur: e.target.value })}
              placeholder="12500"
              data-testid={`${pid("real-cost")}-input`}
            />
          </div>
          <div className="space-y-2 col-span-2">
            <Label htmlFor={pid("effort")}>Esfuerzo total (horas)</Label>
            <Input
              id={pid("effort")}
              type="number"
              value={formData.effort_hours_total || ""}
              onChange={(e) => setFormData({ ...formData, effort_hours_total: e.target.value })}
              placeholder="Se auto-calcula desde las tareas del piloto"
              data-testid={`${pid("effort")}-input`}
            />
          </div>
        </div>
      </div>
    </>
  );
}
